import { ArrowLeft } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import BalanceDetails from '@/components-latest/ui/BillingPage/WalletTransactions/BalanceDetails'
import {
  DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  WalletTransactionsPageSize
} from '@/components-latest/ui/BillingPage/WalletTransactions/constants'
import TransactionDetailsDrawer from '@/components-latest/ui/BillingPage/WalletTransactions/TransactionDetailsDrawer'
import TransactionsList from '@/components-latest/ui/BillingPage/WalletTransactions/TransactionsList'
import { useLazyFetchWalletTransactionDetailsQuery } from '@/ducks/billing/api/billing.api'

import { WalletTransaction } from './types'
import Styles from './WalletTransactions.module.scss'

type BrandFilterValue = 'kool' | 'yassir' | undefined

type Props = {
  onBack?: () => void
  transactions?: WalletTransaction[]
  balanceAmount?: number
  currencyCode?: string
  isLoading?: boolean
  formatDateTime?: (value: string) => string
  page?: number
  limit?: WalletTransactionsPageSize
  offset?: number
  total?: number
  onPageChange?: (page: number) => void
  onLimitChange?: (limit: WalletTransactionsPageSize) => void
  brandFilter?: BrandFilterValue
  onBrandFilterChange?: (brand: BrandFilterValue) => void
}

const WalletTransactions = ({
  onBack,
  transactions = [],
  balanceAmount = 0,
  currencyCode = 'DA',
  isLoading = false,
  formatDateTime,
  page = 0,
  limit = DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  offset = 0,
  total,
  onPageChange,
  onLimitChange,
  brandFilter,
  onBrandFilterChange
}: Props) => {
  const { t } = useTranslation()
  const hasTransactions = transactions.length > 0
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null)
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false)

  const [
    getWalletTransactionDetails,
    {
      data: walletTransactionDetails,
      isFetching: isWalletTransactionDetailsFetching,
      isError: isWalletTransactionDetailsError
    }
  ] = useLazyFetchWalletTransactionDetailsQuery()

  const resolveDateTime = useCallback(
    (value: string) => {
      if (formatDateTime) return formatDateTime(value)
      if (!value) return '-'
      return value
    },
    [formatDateTime]
  )

  useEffect(() => {
    if (!isDetailsDrawerOpen || !selectedTransactionId) return
    getWalletTransactionDetails({ transactionId: selectedTransactionId })
  }, [
    getWalletTransactionDetails,
    isDetailsDrawerOpen,
    selectedTransactionId
  ])

  const handleViewDetails = useCallback((transactionId: string) => {
    setSelectedTransactionId(transactionId)
    setIsDetailsDrawerOpen(true)
  }, [])

  const handleDetailsDrawerOpenChange = useCallback((open: boolean) => {
    setIsDetailsDrawerOpen(open)
    if (!open) {
      setSelectedTransactionId(null)
    }
  }, [])

  return (
    <div className={Styles.card} data-testid="wallet-transactions">
      <div className={Styles.header}>
        <div className={Styles.actions}>
          <div className={Styles.leftHeader}>
            <button
              type="button"
              className={Styles.backButton}
              onClick={onBack}
              aria-label={t('common.cta.back')}>
              <ArrowLeft width={18} height={14} />
              <Text
                variant="nextBody"
                weight="semiBold"
                className={Styles.backText}>
                {t('common.cta.back')}
              </Text>
            </button>
            <div className={Styles.headerDivider} />
            <Text variant="heading-05" weight="semiBold">
              {t('billing.wallet.page-title')}
            </Text>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className={Styles.loadingWrapper}>
          <Spinner />
        </div>
      ) : hasTransactions ? (
        <>
          <BalanceDetails
            balanceAmount={balanceAmount}
            currency={currencyCode}
          />
          <TransactionsList
            transactions={transactions}
            page={page}
            limit={limit}
            offset={offset}
            total={total}
            onPageChange={onPageChange || (() => undefined)}
            onLimitChange={onLimitChange || (() => undefined)}
            onViewDetails={handleViewDetails}
            brandFilter={brandFilter}
            onBrandFilterChange={onBrandFilterChange}
          />
          <TransactionDetailsDrawer
            open={isDetailsDrawerOpen}
            onOpenChange={handleDetailsDrawerOpenChange}
            details={walletTransactionDetails}
            isLoading={isWalletTransactionDetailsFetching}
            isError={isWalletTransactionDetailsError}
            formatDateTime={resolveDateTime}
          />
        </>
      ) : (
        <>
          <div className={Styles.tableHeader}>
            <Text variant="heading-05" weight="semiBold">
              {t('billing.wallet.transactions-title')}
            </Text>
          </div>
          <div className={Styles.emptyState}>
            <img
              src="/images/billing/empty-transactions.svg"
              alt={t('billing.wallet.no-transactions-title')}
              className={Styles.emptyIllustration}
            />
            <Text variant="nextTitle-4" weight="semiBold" align="center">
              {t('billing.wallet.no-transactions-title')}
            </Text>
            <Text
              variant="nextBody"
              align="center"
              className={Styles.emptySubtitle}>
              {t('billing.wallet.no-transactions-subtitle')}
            </Text>
          </div>
        </>
      )}
    </div>
  )
}

export default WalletTransactions
